<template>
  <v-flex
    id="seniorReflection"
    class="question"
  >
    <h2 class="display-3 display-3_dark mt-4">
      {{ getSeniorReflectionQuestions.label }}
    </h2>
    <ul class="questions-list">
      <li
        v-if="getSeniorReflectionQuestions.hasOwnProperty('is_loneliness')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getSeniorReflectionQuestions.is_loneliness.required }"
        >
          {{ getSeniorReflectionQuestions.is_loneliness.label }}
        </p>
        <ConfirmButtons
          v-model="isLoneliness"
          v-validate="{ required: getSeniorReflectionQuestions.is_loneliness.required }"
          string
          :error-messages="errors.collect('isLoneliness')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'isLoneliness'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="getSeniorReflectionQuestions.hasOwnProperty('ready_to_change')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getSeniorReflectionQuestions.ready_to_change.required }"
        >
          {{ getSeniorReflectionQuestions.ready_to_change.label | addColons }}
        </p>
        <v-radio-group
          v-model="readyToChange"
          v-validate="{ ...regularField(getSeniorReflectionQuestions.ready_to_change.required) }"
          :error-messages="errors.collect('ready_to_change')"
          :data-vv-as="$t('label.question')"
          data-vv-name="ready_to_change"
          :required="getSeniorReflectionQuestions.ready_to_change.required"
          :disabled="readOnly"
        >
          <v-radio
            v-for="(item, index) in readyToChangeList"
            :key="index"
            :label="item.description"
            :value="item.id"
          />
        </v-radio-group>
        <p class="f-05 fw-400">
          {{ $t('sheet.sheetOne.reflection.senior.ready_to_change_hint') }}
        </p>
      </li>
      <li
        v-if="getSeniorReflectionQuestions.hasOwnProperty('has_preclude_factors')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getSeniorReflectionQuestions.has_preclude_factors.required }"
        >
          {{ getSeniorReflectionQuestions.has_preclude_factors.label }}
        </p>
        <ConfirmButtons
          v-model="hasPrecludeFactors"
          v-validate="{ required: getSeniorReflectionQuestions.has_preclude_factors.required }"
          string
          :error-messages="errors.collect('hasPrecludeFactors')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'hasPrecludeFactors'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          getSeniorReflectionQuestions.hasOwnProperty('preclude_factors') &&
            hasPrecludeFactors === 'yes'
        "
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getSeniorReflectionQuestions.preclude_factors.required }"
        >
          {{ getSeniorReflectionQuestions.preclude_factors.label }}
        </p>
        <v-checkbox
          v-for="(item, index) in precludeFactorList"
          :key="index"
          v-model="precludeFactors"
          v-validate="{ required: getSeniorReflectionQuestions.preclude_factors.required }"
          :label="item.description"
          :value="item.id"
          multiple
          :error="errors.has('precludeFactors')"
          :data-vv-as="' '"
          :data-vv-name="'precludeFactors'"
          :error-messages="
            index + 1 === precludeFactorList.length ? errors.collect('precludeFactors') : null
          "
          :disabled="readOnly"
        />
        <div
          v-if="
            getSeniorReflectionQuestions.hasOwnProperty('other_factors') &&
              showOtherFactors === true
          "
        >
          <p
            class="fw-600 mt-3 mb-0"
            :class="{ required: getSeniorReflectionQuestions.other_factors.required }"
          >
            {{ getSeniorReflectionQuestions.other_factors.label }}
          </p>
          <v-text-field
            v-model="otherFactors"
            v-validate="{ ...regularLongText(getSeniorReflectionQuestions.other_factors.required) }"
            :placeholder="$t('placeholders.add_text')"
            :error-messages="errors.collect('other_preclude_factors')"
            :data-vv-as="$t('label.question')"
            data-vv-name="other_preclude_factors"
            :required="getSeniorReflectionQuestions.other_factors.required"
            :disabled="readOnly"
          />
        </div>
      </li>
    </ul>
    <v-divider class="my-4" />
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';

import filters from '@/constants/filters';
import validationRules from '@/constants/validationRules';

import { createHelpers } from 'vuex-map-fields';
import { mapGetters } from 'vuex';

import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';
import sheetResourcesGetter from '@/mixins/sheet/sheetResourcesGetter';

const { mapFields } = createHelpers({
    getterType: 'sheet/getSeniorReflectionField',
    mutationType: 'sheet/updateSeniorReflectionField'
});

export default {
    name: 'SeniorReflection',
    components: {
        ConfirmButtons
    },
    filters: {
        ...filters
    },
    mixins: [sheetValidationRules, sheetResourcesGetter],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        }
    },
    data () {
        return {
            validationRules
        };
    },
    watch: {
        showOtherFactors (val) {
            if (val === false) {
                this.otherFactors = null;
            }
        },
        hasPrecludeFactors (val) {
            if (val === 'no') {
                this.$nextTick(() => {
                    this.precludeFactors = [];
                });
            }
        }
    },
    computed: {
        ...mapFields([
            'isLoneliness',
            'readyToChange',
            'hasPrecludeFactors',
            'precludeFactors',
            'otherFactors'
        ]),
        ...mapGetters('sheet', ['getSeniorReflectionQuestions']),
        showOtherFactors () {
            return this.precludeFactors.includes(133);
        },
        readyToChangeList () {
            return this.getResources(this.getSeniorReflectionQuestions.ready_to_change);
        },
        precludeFactorList () {
            return this.getResources(this.getSeniorReflectionQuestions.preclude_factors);
        }
    }
};
</script>

<style scoped></style>
